export type ClassroomDeadline =
  | { kind: "empty" }
  | { kind: "valid"; due_date: { year: number; month: number; day: number }; due_time: { hours: number; minutes: number } }
  | { kind: "invalid" | "nonexistent" | "ambiguous"; message: string };

function utcValue(year: number, month: number, day: number, hours: number, minutes: number): number {
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(hours, minutes, 0, 0);
  return date.getTime();
}

/** Resolve a course-local minute, independent of the browser's timezone.
 * Round-trip candidate offsets instead of guessing through a DST transition.
 * A missing or repeated local time needs a new, unambiguous user choice.
 */
export function classroomDeadline(value: string, timeZone: string): ClassroomDeadline {
  if (!value) return { kind: "empty" };
  const invalid = (): ClassroomDeadline => ({ kind: "invalid", message: `Enter a valid date and time in ${timeZone}.` });
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return invalid();
  const [year, month, day, hours, minutes] = match.slice(1).map(Number);
  const wall = utcValue(year, month, day, hours, minutes);
  const check = new Date(wall);
  if (year < 1 || check.getUTCFullYear() !== year || check.getUTCMonth() + 1 !== month || check.getUTCDate() !== day || check.getUTCHours() !== hours || check.getUTCMinutes() !== minutes) return invalid();
  try {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    });
    const localWall = (instant: number): number => {
      const parts = Object.fromEntries(formatter.formatToParts(instant).map(part => [part.type, part.value]));
      return utcValue(Number(parts.year), Number(parts.month), Number(parts.day), Number(parts.hour), Number(parts.minute));
    };
    // Cover both sides of nearby offset changes, including half-hour zones.
    const offsets = new Set([-36, -24, -12, 0, 12, 24, 36].map(hoursFromWall => {
      const probe = wall + hoursFromWall * 3_600_000;
      return localWall(probe) - probe;
    }));
    const matches = [...offsets].map(offset => wall - offset).filter(instant => localWall(instant) === wall);
    if (matches.length === 0) return { kind: "nonexistent", message: `This time does not exist in ${timeZone} because the clocks move forward. Choose another local time.` };
    if (matches.length !== 1) return { kind: "ambiguous", message: `This time occurs twice in ${timeZone} when the clocks change. Choose an unambiguous local time.` };
    const instant = new Date(matches[0]);
    return {
      kind: "valid",
      due_date: { year: instant.getUTCFullYear(), month: instant.getUTCMonth() + 1, day: instant.getUTCDate() },
      due_time: { hours: instant.getUTCHours(), minutes: instant.getUTCMinutes() },
    };
  } catch {
    return invalid();
  }
}
